/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/itpPreviewImageRemove.ts
 * FILE_ID: JOBHUB-QA-ITP-PREVIEW-REMOVE-001
 * PURPOSE: Corner cross on the live ITP preview. The saved copy has no cross.
 */
import { markPreviewPhotosCleared } from '../../../qa/itpPreviewRemoved'

const COVER_WRAP = 'display: block; position: relative; width: fit-content; max-width: 100%; margin-left: auto; margin-right: auto; padding: 0; border: 0'
const PHOTO_WRAP = 'display: block; position: relative; width: fit-content; max-width: 100%; margin: 0; padding: 0; border: 0'

const REMOVE_CSS = [
  '.itp-image-remove { position: absolute; top: 0; right: 0; z-index: 2; width: 24px; height: 24px; margin: 0; padding: 0; border: 0; background: transparent; display: flex; align-items: center; justify-content: center; cursor: pointer; }',
  '.itp-image-remove.is-touch { width: 44px; height: 44px; }',
  '.itp-image-remove span { width: 20px; height: 20px; border: 1px solid #ddd; border-radius: 999px; background: #fff; color: #dc2626; display: flex; align-items: center; justify-content: center; line-height: 1; font-size: 14px; }',
].join('\n')

export function confirmRemoveImage(kind: 'cover' | 'check'): boolean {
  const message = kind === 'check'
    ? 'Remove this image from this preview?'
    : 'Remove this image?'
  // Reused from: packages/job-hub/src/jobs/tabs/job-qa/itp-export/QaItpExportView.tsx (back)
  return window.confirm(message)
}

// Reused from: packages/ui/src/qa/QaPhotoTile.tsx (Remove photo button)
function makeButton(doc: Document, kind: 'cover' | 'check', touch: boolean): HTMLButtonElement {
  const button = doc.createElement('button')
  button.type = 'button'
  button.className = touch ? 'itp-image-remove is-touch' : 'itp-image-remove'
  button.setAttribute('aria-label', 'Remove image')
  button.contentEditable = 'false'
  button.dataset.kind = kind
  const mark = doc.createElement('span')
  mark.setAttribute('aria-hidden', 'true')
  mark.textContent = '×'
  button.appendChild(mark)
  return button
}

function ensureStyle(doc: Document): void {
  if (doc.querySelector('style[data-itp-remove]')) return
  const style = doc.createElement('style')
  style.setAttribute('data-itp-remove', '')
  style.textContent = REMOVE_CSS
  ;(doc.head ?? doc.documentElement).appendChild(style)
}

function hostHasButton(host: Element): boolean {
  return host.querySelector(':scope > .itp-image-remove') != null
}

function wrap(img: HTMLImageElement, style: string): HTMLElement {
  const shot = img.ownerDocument.createElement('span')
  shot.className = 'itp-preview-shot'
  shot.setAttribute('style', style)
  img.replaceWith(shot)
  shot.appendChild(img)
  return shot
}

export function attachPreviewRemoveButtons(doc: Document, touch: boolean): void {
  ensureStyle(doc)
  doc.querySelectorAll('img.itp-cover-photo').forEach((node) => {
    const img = node as HTMLImageElement
    if (img.closest('.itp-preview-shot')) return
    const host = wrap(img, COVER_WRAP)
    if (!hostHasButton(host)) host.appendChild(makeButton(doc, 'cover', touch))
  })
  doc.querySelectorAll('.photos img').forEach((node) => {
    const img = node as HTMLImageElement
    if (img.closest('.itp-org-logo, .itp-quotech-logo, .signoff')) return
    const frame = img.closest('.frame')
    if (frame instanceof HTMLElement) {
      if (hostHasButton(frame)) return
      frame.style.position = 'relative'
      frame.appendChild(makeButton(doc, 'check', touch))
      return
    }
    const existing = img.closest('.itp-preview-shot')
    const host = existing ?? wrap(img, PHOTO_WRAP)
    if (!hostHasButton(host)) host.appendChild(makeButton(doc, 'check', touch))
  })
}

export function removePreviewCheckPhoto(img: HTMLImageElement): void {
  const block = img.closest('.photos')
  const figure = img.closest('figure')
  if (figure && block?.contains(figure)) figure.remove()
  else {
    const shot = img.closest('.itp-preview-shot')
    if (shot) shot.remove()
    else img.remove()
  }
  if (block && block.querySelector('img') == null) {
    // Reused from: packages/job-hub/src/jobs/qa/itpPreviewRemoved.ts (markPreviewPhotosCleared)
    markPreviewPhotosCleared(block)
  }
}

export function stripPreviewRemoveButtons(doc: Document): void {
  doc.querySelectorAll('.itp-image-remove').forEach((node) => node.remove())
  doc.querySelectorAll('.itp-preview-shot').forEach((shot) => {
    const img = shot.querySelector('img')
    if (img) shot.replaceWith(img)
    else shot.remove()
  })
  doc.querySelector('style[data-itp-remove]')?.remove()
}

export function savedPreviewHtml(doc: Document): string {
  const copy = new DOMParser().parseFromString(
    `<!doctype html>\n${doc.documentElement.outerHTML}`,
    'text/html',
  )
  stripPreviewRemoveButtons(copy)
  copy.querySelectorAll('.itp-hold-sign').forEach((node) => node.remove())
  return `<!doctype html>\n${copy.documentElement.outerHTML}`
}
